import React from 'react'

import { Formik, Field, ErrorMessage } from 'formik'
import { Form, Row, Col, Button } from 'react-bootstrap'
import { useHistory } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import * as Yup from 'yup'

import { IRetailer } from 'src/services/types/retailer'
import RetailerService from 'src/services/RetailerService'
import { route } from 'src/utils/routes'
import { useSelector } from 'react-redux'
import { IState } from 'src/store'

const retailerService = new RetailerService()

interface IProps {
  type?: string
  retailer?: IRetailer
  updateRetailer?: (updatedValues: IRetailer) => void
}

const RetailerForm: React.FC<IProps> = ({ type, retailer, updateRetailer }) => {
  const history = useHistory()
  const { addToast } = useToasts()
  const user = useSelector((state: IState) => state.user)

  const formConfig = {
    initialValues: {
      name: retailer ? retailer.name : '',
      status: retailer ? retailer.status : 'active',
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .required('Name field is required.')
        .min(3, 'Name must be at least 3 characters.'),
      status: Yup.string().required('Status field is required.'),
    }),
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true)
      const finalValues: any = {
        name: values.name,
        status: values.status,
        manufacturer_id: user.manufacturer_id,
      }

      if (type === 'update' && retailer) {
        finalValues._id = retailer._id
      }

      retailerService[type]({ ...finalValues })
        .then((retailerId) => {
          setSubmitting(false)

          if (type === 'update') {
            addToast('Retailer successfully updated.', {
              appearance: 'success',
            })
            updateRetailer(values)
          } else {
            addToast('Retailer successfully created.', {
              appearance: 'success',
            })
            history.push(route('retailer_update', { id: retailerId }))
          }
        })
        .catch((error) => {
          setSubmitting(false)
          addToast(error.message, { appearance: 'error' })
          throw error
        })
    },
  }

  return (
    <Formik
      enableReinitialize={true}
      initialValues={formConfig.initialValues}
      validationSchema={formConfig.validationSchema}
      onSubmit={formConfig.onSubmit}
    >
      {({ errors, touched, values, handleSubmit, ...formik }) => (
        <Form onSubmit={handleSubmit}>
          {/* Retailer status */}
          <Form.Group as={Row} className="align-items-center">
            <Col md={3} className="mb-2 mb-md-0">
              <Form.Label>Status *</Form.Label>
            </Col>
            <Col md={6}>
              <div className="d-flex">
                <Field
                  as={Form.Check}
                  type="radio"
                  id="active"
                  name="status"
                  value="active"
                  label="Active"
                  checked={values.status === 'active'}
                  custom
                />

                <Field
                  as={Form.Check}
                  type="radio"
                  id="inactive"
                  name="status"
                  value="inactive"
                  label="Inactive"
                  checked={values.status === 'inactive'}
                  className="ml-3"
                  custom
                />
              </div>
            </Col>
          </Form.Group>
          <hr />

          {/* Retailer name */}
          <Form.Group as={Row}>
            <Col md={3} className="mb-2 mb-md-0">
              <Form.Label>Name *</Form.Label>
              <Form.Text className="text-muted">Name of retailer</Form.Text>
            </Col>
            <Col md={6}>
              <Field
                as={Form.Control}
                name="name"
                value={values.name}
                type="text"
                isInvalid={touched.name && errors.name ? true : false}
              />
              <ErrorMessage
                name="name"
                component="span"
                className="invalid-feedback"
              />
            </Col>
          </Form.Group>
          <hr />

          <Button
            type="submit"
            variant="success"
            className="mt-4"
            disabled={formik.isSubmitting || !formik.dirty || !formik.isValid}
          >
            {formik.isSubmitting ? (
              <figure className="spinner button white" />
            ) : type === 'create' ? (
              'Create Retailer'
            ) : (
              'Update Retailer'
            )}
          </Button>
        </Form>
      )}
    </Formik>
  )
}

RetailerForm.defaultProps = {
  type: 'create',
}

export default RetailerForm
